import type { Suggestion } from "./types";
import type { CompanionApiClient } from "./ranking-client";

// Once per suggestion per IMPRESSION_RESEND_MS — not per poll / per peek nav.
// Re-sending on every 2.5-5s refresh was ~1-2 req/s per viewer (each doubled by
// the CORS preflight) into nginx's 2r/s zone, and it inflated the funnel's
// impression side for picks the user was already looking at.
export const IMPRESSION_RESEND_MS = 30 * 60 * 1000;

/** Same impression event whether the pick was seen in the full sheet or the
 * peek — same fields (event_id, rec_type, legs, combined_odds, dims), same
 * dedup — so a peek impression is indistinguishable downstream from a sheet
 * impression and the strategy table / dimension slices don't split into two
 * incompatible populations. `rank` is the position the user is actually
 * looking at it in — the sheet's row index, or the peek's card index. */
/** The legs an EVENT carries: only the identity the backend reads (event,
 *  market, code, line, stake, selection, part, price, liveness, sport).
 *  ⛔ 2026-10-13: whole legs (reasoning, prices, names, facts — twice, as `legs`
 *  and `advice_legs`) made a 5-leg multi-match impression ~11.6 KB against the
 *  4096-byte events cap (app.py:2787), so every one was refused. */
export function eventLegs(legs: Suggestion["legs"]): Array<Record<string, unknown>> | undefined {
  if (!legs) return undefined;
  return legs.map((l) => {
    const r = l as unknown as Record<string, unknown>;
    return {
      event_id: r.event_id, market_id: r.market_id ?? null, code: r.code ?? null,
      line: r.line ?? null, stake_id: r.stake_id, selection: r.selection,
      market: r.market, odds: r.odds,
      ...(r.part ? { part: r.part } : {}),
      ...(typeof r.is_live === "boolean" ? { is_live: r.is_live } : {}),
      ...(r.sport_en ? { sport_en: r.sport_en } : {}),
    };
  });
}

type DiagnosticClient = Pick<CompanionApiClient, "sendEvent"> &
  Partial<Pick<CompanionApiClient, "sendFactDiagnosticExposure">>;

/** Retry exact visible evidence until acknowledged, separately from funnel dedup. */
export function makeFactDiagnosticSender(
  client: DiagnosticClient,
  onCurrentMatch?: (s: Suggestion) => boolean | null,
): (s: Suggestion) => void {
  const captured = new Map<string, { fingerprint: string; at: number }>();
  const pending = new Map<string, string>();
  const unavailable = new Map<string, string>();
  return (s): void => {
    if (s.type !== "fact" || !client.sendFactDiagnosticExposure
        || !s.event_id || !/^[a-f0-9]{64}$/.test(s.fact_diagnostic_id ?? "")) return;
    if (unavailable.get(s.id) === s.fact_diagnostic_id) return;
    const traceId = s.fact_diagnostic_id!;
    const current = onCurrentMatch?.(s) ?? null;
    // Reference hashes also contain request diagnostics. Only an actual change
    // in the visible card requires another capture inside the impression window.
    const fingerprint = JSON.stringify([s.event_id, s.home, s.away, s.fact_lines, s.reasoning, current]);
    const previous = captured.get(s.id);
    if (previous?.fingerprint === fingerprint && Date.now() - previous.at < IMPRESSION_RESEND_MS) return;
    if (pending.get(s.id) === fingerprint) return;
    pending.set(s.id, fingerprint);
    void client.sendFactDiagnosticExposure(s.id, String(s.event_id ?? ""), {
      id: traceId, on_current_match: current,
    }).then((ok) => {
      if (ok === null) unavailable.set(s.id, traceId);
      if (ok === true && pending.get(s.id) === fingerprint) captured.set(s.id, { fingerprint, at: Date.now() });
    }).catch(() => { /* next actual visible observation retries */ }).finally(() => {
      if (pending.get(s.id) === fingerprint) pending.delete(s.id);
    });
  };
}

export function makeImpressionSender(
  client: DiagnosticClient,
  dimsOf: (s: Suggestion, rank: number) => Record<string, unknown>,
  exposureIdOf?: () => string | null,
  presentationOf?: () => { presentation: string; expand_reason: string },
  onCurrentMatch?: (s: Suggestion) => boolean | null,
  captureDiagnostic?: (s: Suggestion) => void,
): (s: Suggestion, rank: number) => void {
  const capture = captureDiagnostic ?? makeFactDiagnosticSender(client, onCurrentMatch);
  const impressionSent = new Map<string, number>(); // suggestion id → last sent ts
  return (s: Suggestion, rank: number): void => {
    capture(s);
    const lastSent = impressionSent.get(s.id);
    if (lastSent != null && Date.now() - lastSent < IMPRESSION_RESEND_MS) return;
    impressionSent.set(s.id, Date.now());
    const exposureId = exposureIdOf?.() ?? null;
    client.sendEvent("", "impression", s.id, null, {
      event_id: s.event_id,
      rec_type: s.rec_type,
      ...(s.type === "fact" ? { fact_diagnostics: {
        id: s.fact_diagnostic_id ?? null,
        on_current_match: onCurrentMatch?.(s) ?? null,
      } } : {}),
      legs: eventLegs(s.legs),
      // Singles have no `legs` array. Preserve the exact shown outcome so the
      // stats page can distinguish "shown advice" from a fixture merely claimed
      // later through navigation or visit attribution.
      // Measured 2026-10-02, a corners/cards single is market_id 3 like a goals
      // total, so without the part the shown card could not be told from a goals line.
      advice_legs: s.type === "parlay" ? eventLegs(s.legs) : [{
        event_id: s.event_id, market_id: s.market_id, argument: s.line,
        code: s.code, selection: s.selection, market: s.market,
        ...(typeof s.part === "string" && s.part ? { part: s.part } : {}),
      }],
      combined_odds: s.combined_odds,
      dims: {
        ...dimsOf(s, rank),
        ...(exposureId ? { exposure_id: exposureId } : {}),
        // ⛔ AN ADVICE CARD IS NEVER A COMPACT IMPRESSION. The plan's rule is
        // that the compact launcher must not be counted as an advice-card
        // impression — so the two carry the dimension that separates them,
        // rather than the reader inferring it from the event name.
        ...(presentationOf ? presentationOf() : {}),
      },
    });
  };
}
